import React, { useContext, useEffect, useState } from 'react'
import analytics from '../../config/analytics'
import usePrevious from '../hooks/usePrevious'
import useImageEffect from '../hooks/useImageEffect'
import Heading from '@react/react-spectrum/Heading'
import IconImage from '@react/react-spectrum/Icon/Image'
import { Image } from '@react/react-spectrum/Image'
import { v4 as uuidv4 } from 'uuid';
import { EasybakeContext } from '../EasybakeProvider'
import Link from '../Nav/Link'
import Spinner from '../Nav/Spinner'
import ImageWait from '../Nav/ImageWait'
import { __ } from 'i18n-for-browser'
import '../../config/i18n'
import CodeSnippet from '../Nav/CodeSnippet'
import code from '../../asset/code/psactions.txt'
import MultilineText from '../Nav/MultilineText'
import CacheMultilineText from '../Nav/CacheMultilineText'
import PsActionsCustom from './PsActionsCustom'
import CompareImage from '../Nav/CompareImage'

import { 
    getEncodedURI, 
    logAlert,
    logMsg,
    getSpinnerCustomStyle,
    runAperitifOperation
} from '../../lib/easybake'

import { 
    DEFAULT_DOC_WIDTH,
    DEFAULT_DOC_HEIGHT
} from '../../lib/constants'

export default function PsActions () {
    const { 
        dispatch,
        spinnerTimeout,
        group,
        wait,
        action,
        isApiReady,
        isApiDone,
        customFiles,
        customAfterImage,
        ut,
        page,
        aperitif,
        utStorage
    } = useContext(EasybakeContext)
    let { beforeImage, afterImage } = useImageEffect({ group: 'psactions' })
    const caption = `${__('psactions_applying')}\u2026` // suffixed by a horizontal ellipse
    let descriptionKey = 'psactions_desc'
    let prevImageSet = usePrevious({ beforeImage, afterImage })

    useEffect( () => {
        if (action === "psactionscustom" && isApiReady && !isApiDone && !customAfterImage) {
            const startDate = new Date()
            runApi( (res) => {
                const endDate = new Date()
                var seconds = Math.round((endDate.getTime() - startDate.getTime()) / 1000)
                logMsg(null, `${page}:seconds:${seconds}`)
                
                analytics.sendEvent(
                    {
                        type: 'api',
                        action: 'api.psActions',
                        page: page,
                        file: null,
                        response: res & res.status ? res.status : 'unknown',
                        elapsedTime: seconds
                    }
                )

                analytics.sendEventName('api', `${page}:elapsedTime:${analytics.getElapsedTime(seconds)}`)
                dispatch({ type: 'wait', payload: false })
                if (res && res.error) {
                    analytics.sendEventName('api', `${page}:error`)
                    if (seconds > 120) {
                        logAlert(null, `${res.msg} - Timed out`)
                    } else {
                        logAlert(null, res.msg)
                    }
                }
            })
        }
    }, [action, aperitif, utStorage, isApiReady])

    function getPreviewHeight() {
        if (action === 'psactionscustom') {
            return `${customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0].height}px` || '100%'
        } else {
            return `${DEFAULT_DOC_HEIGHT}px`
        }
    }

    function getActionDesc (action) {
        switch (action) {
        case 'bannergeneration':
            descriptionKey = 'psactions_desc_bannergeneration'
            break
        case 'camerarawfilter':
            descriptionKey = 'psactions_desc_camerarawfilter'
            break
        case 'graphicdesign':
            descriptionKey = 'psactions_desc_graphicdesign'
            break
        default:
            break
        }
    }

    async function psActionsPre() {
        beforeImage = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0].thumbnail
        afterImage = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0].url
        prevImageSet = { beforeImage, afterImage }
    }

    async function runApi(cb) {
        try {
            logMsg(null, 'running API...')
            const uuid = uuidv4()
            const inputImageFile = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0]
            const inputActionFile = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'action')[0]
            
            beforeImage = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0].thumbnail
            prevImageSet = { beforeImage, afterImage }

            // const sdk = require('@adobe/aio-lib-photoshop-api')
            // const client = await sdk.init(null, process.env.REACT_APP_CONFIG_APP_KEY, ut || aperitif.userToken)

            const bodyObject = {
                inputs: [
                    {
                        href: ut ? utStorage.urls[0].get : aperitif.urls[0].get,
                        storage: inputImageFile.storage
                    }
                ],
                options: {
                    actions: [
                        {
                            href: ut ? utStorage.urls[1].get : aperitif.urls[1].get,
                            storage: inputActionFile.storage
                        }
                    ]
                },
                outputs: [
                    {
                        href: ut ? utStorage.urls[2].put : aperitif.urls[2].put,
                        width: DEFAULT_DOC_WIDTH,
                        storage: 'external',
                        type: 'image/png',
                        compression: "large",
                        overwrite: true
                    }
                ]
            }
            const res = await runAperitifOperation(ut, ut || aperitif.userToken, bodyObject)
            if (!res.error) {
                if (res.job.outputs && res.job.outputs.length > 0 && res.job.outputs[0].status) {
                    const output = res.job.outputs[0]
                    analytics.sendEventName('api', `${page}:response:${output.status}`)
                    if (output.status === 'succeeded') {
                        afterImage = ut ? utStorage.urls[2].get : aperitif.urls[2].get
                        logMsg(null, `afterImage: ${afterImage}`)
                        prevImageSet = { beforeImage, afterImage }
                        dispatch({ type: 'set-customAfterImage', payload: {customAfterImage: afterImage} })
                    } else {
                        logMsg(null, res.job.jobId)
                        dispatch({ type: 'set-isApiReady', payload: {isApiReady: false} })
                        cb({
                            error: true,
                            msg: `${output.status.toUpperCase()} - ${output.errors.code}: ${output.errors.title}`,
                            job: res.job || null
                        })
                    }
                }
            } else {
                dispatch({ type: 'set-isApiReady', payload: {isApiReady: false} })
                cb({
                    error: true,
                    msg: __('msg_error_general'),
                    job: res.job || null
                })
            }
            dispatch({ type: 'set-isApiDone', payload: {isApiDone: true} })
            cb({
                error: false,
                msg: "",
                job: null
            })
        } catch(e) {
            dispatch({ type: 'set-isApiReady', payload: {isApiReady: false} })
            cb({
                error: true,
                msg: __('msg_error_general'),
                job: null
            })
        }
    }

    function getComps() {
        if (group === 'psactions') {
            if (action === 'psactionscustom') {
                if (wait) {
                    return null
                } else {
                    return <React.Fragment>
                        <div className="adobe-easybake-image-preview-custom">
                            <CompareImage spinnerCaption={caption} before={beforeImage} after={customAfterImage || afterImage} />
                        </div>
                        <div className="adobe-easybake-caption">{ getActionDesc(action) }</div>
                    </React.Fragment>
                }
            } else {
                if (!wait) {
                    return <div className="adobe-easybake-caption-container">
                        <ImageWait caption={caption} className="adobe-easybake-original-img" src={afterImage} alt="processed img"/>
                        <div className="adobe-easybake-caption">{ getActionDesc(action) }</div>
                    </div>
                }
            }
        } else {
            return <ImageWait caption={`${__('loading_image')}\u2026`} className="adobe-easybake-original-img" src={beforeImage} alt="original img"/>
        }
    }

    function getImageTitle() {
        const name = customFiles && customFiles.length > 0 && customFiles.filter( file => file.assetType === 'image')[0].name
        return name || __('psactions_image_title')
    }

    function tryOtherFiles(e) {
        e.preventDefault()
        analytics.sendEventName('click', `${page}:tryotherfiles`)
        dispatch({ type: 'set-action', payload: { group, action: "psactionscustom", on: true } })
    }

    if (action === "psactionscustom" && !isApiReady) {
        return <PsActionsCustom />
    } else {
        if (action === "psactionscustom" && customFiles && customFiles.length > 0 && isApiReady) {
            psActionsPre()
        }
        return <div className="adobe-easybake-main-pane">
            <div className="adobe-easybake-main adobe-easybake-preset">
                <CacheMultilineText localizationKey={descriptionKey}/>
                <div className="adobe-easybake-file-info-custom">
                    <div className="adobe-easybake-file-info">
                        <div className="adobe-easybake-icon-image-container">
                            <IconImage className="adobe-easybake-icon-image" size="S" />
                        </div>
                        <div className="adobe-easybake-file-name">{ getImageTitle() }</div>
                    </div> 
                    <div className="adobe-easybake-file-info">
                        { customAfterImage ?
                        <div className="adobe-easybake-click-action" onClick={ e => tryOtherFiles(e) }>{__('try_other_files')}</div>
                        : null}
                    </div>
                </div>
                {group === 'psactions' && action !== "psactionscustom" && wait &&
                    <div className="adobe-easybake-image-preview" >
                        <Image className="adobe-easybake-original-img" src={prevImageSet.beforeImage} alt="processed img"/>
                        <Spinner timeout={action === "psactionscustom" ? null : spinnerTimeout} caption={caption} className="spinner" />
                    </div>
                }
                {group === 'psactions' && action === "psactionscustom" && wait &&
                    <div className="adobe-easybake-image-preview-custom" height={ getPreviewHeight() }>
                        <Image className="adobe-easybake-original-img-custom" src={prevImageSet.beforeImage} alt="processed img"/>
                        <Spinner timeout={action === "psactionscustom" ? null : spinnerTimeout} 
                            className="spinner"
                            caption={caption} 
                            style={ getSpinnerCustomStyle (DEFAULT_DOC_WIDTH, DEFAULT_DOC_HEIGHT, customFiles) } />
                    </div>
                }
                { getComps() }
                {group === 'psactions' && !wait &&
                    <div className="adobe-easybake-api-detail">
                        <div className="adobe-easybake-code-preview">
                            <CodeSnippet language="json" url={code}/>
                        </div>
                        <div className="adobe-easybake-desc">
                            <Heading variant="subtitle2">{__('psactions_title')}</Heading>
                            <MultilineText localizationKey={descriptionKey}/>
                            <Link className="adobe-easybake-desc-link" referrer={`documentation:${group}:${action}:main`} label={__('link_doc')} url={__('url_doc_psactions')} />
                        </div>
                    </div>
                }
            </div>
        </div>
    }
}
